/**
 * A report opened by its secret link: no account, no shell, no project (N13).
 *
 * Everything this page knows comes from `/api/r/{secret}`, which answers with the page and its
 * inputs and nothing else. The page reads only those fields, so a payload that ever grew a
 * project or an organization still could not leak one.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { Box, Center, Container, Image, Stack } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { usePublicReport } from '@/api/reports';
import { publicReportViewerLink, publicReportViewerToken } from '@/api/viewer';
import { EmptyState } from '@/components/EmptyState';
import { PageLoader } from '@/components/PageLoader';
import { layerRefs, fromPublicReport, ReportPage } from '@/features/reports/ReportPage';
import { useReportViewer } from '@/features/reports/useReportViewer';
import { brand } from '@/theme/theme';

export const Route = createFileRoute('/r/$secret')({ component: AnonymousRoute });

function AnonymousRoute() {
  const { secret } = Route.useParams();
  return <AnonymousReportPage secret={secret} />;
}

export function AnonymousReportPage({ secret }: { secret: string }) {
  const { t, i18n } = useTranslation('reports');
  const query = usePublicReport(secret);
  const locale = query.data?.locale;

  // The report's own language, for this page only: the device's stored locale belongs to
  // whoever uses this browser, not to the link.
  useEffect(() => {
    if (locale) void i18n.changeLanguage(locale);
  }, [locale, i18n]);
  useEffect(() => {
    document.title = 'Forestum';
  }, []);

  const view = useMemo(() => (query.data ? fromPublicReport(query.data) : null), [query.data]);
  const fetchToken = useCallback(() => publicReportViewerToken(secret), [secret]);
  const fetchLink = useCallback((object: string, path: string) => publicReportViewerLink(secret, object, path), [secret]);
  const { source, linkSource, credential, error: tokenError } = useReportViewer(fetchToken, fetchLink);
  const refs = useMemo(() => (view ? layerRefs(credential?.objects, view) : []), [credential, view]);

  return (
    <Box bg={brand.bg} mih="100vh">
      <Box bg={brand.surface} py="sm" className="report-header" style={{ borderBottom: `1px solid ${brand.border}` }}>
        <Container size="lg" px={32}>
          <Image src="/logo-green.png" alt="Forestum" h={32} w="auto" fit="contain" />
        </Container>
      </Box>
      <Container size="lg" px={32} py="xl">
        {query.isPending ? (
          <Center py="xl">
            <PageLoader />
          </Center>
        ) : query.isError || !view ? (
          <EmptyState title={t('anonymous.invalid')} description={t('anonymous.invalidHint')} />
        ) : (
          <Stack gap="md">
            <ReportPage
              report={view}
              refs={refs}
              tokenSource={source}
              linkSource={linkSource}
              tokenError={!!tokenError}
              viewKey={`anon-${secret.slice(0, 12)}`}
              granted={credential?.objects}
              fetchLink={fetchLink}
            />
          </Stack>
        )}
      </Container>
    </Box>
  );
}
